import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { adminMenuChildPropTypes } from '../AdminMenu/admin-menu-prop-types';
import { classNames } from '../../utilities';
import { NotificationsCount, NotificationsLink } from '../NotificationsLink';

import { MenuList } from './MenuList';
import styles from './Menu.css';

export class Menu extends Component {
  constructor() {
    super();

    this.handleSubMenuOrder = this.handleSubMenuOrder.bind(this);
    this.handleMenuPosition = this.handleMenuPosition.bind(this);
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
    this.handleSubNavToggle = this.handleSubNavToggle.bind(this);

    this.state = {
      menuOpen: false,
      menuOrder: ['Admin Menu'],
      menuPosition: 0
    };
  }

  componentDidMount() {
    this.htmlElement = document.querySelector('html');
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  handleSubMenuOrder(currentMenu) {
    const { menuOrder } = this.state;

    this.setState({ menuOrder: [...menuOrder, currentMenu] });
  }

  handleMenuPosition(back) {
    this.setState(prevState => {
      // 30(rem) is the width of the menu
      const modifier = back ? 30 : -30;

      return { menuPosition: parseInt(prevState.menuPosition) + modifier };
    });

    if (back) {
      const { menuOrder } = this.state;
      const updatedMenuOrder = menuOrder.filter(menu => menu !== menuOrder[menuOrder.length - 1]);

      this.setState({ menuOrder: updatedMenuOrder });
    }
  }

  handleMenuToggle() {
    const { menuOpen, menuOrder } = this.state;

    this.setState({ menuOpen: !menuOpen });
    this.htmlElement.classList.toggle(styles.htmlLocked);

    if (menuOpen) {
      // Wait for the menu to close before closing the sub-nav, otherwise it looks a little jarring.
      setTimeout(() => {
        // Sometimes the Menu will stay mounted if we don't reload the page on navigation, e.g. when
        // using React Router's HashRouter.
        if (!this.isUnmounted) {
          this.setState({ menuOrder: [menuOrder[0]], menuPosition: 0 });
        }
      }, 200);
    }
  }

  handleSubNavToggle(currentMenu) {
    this.handleSubMenuOrder(currentMenu);
    this.handleMenuPosition();
  }

  render() {
    const {
      config: { menuItems, notifications },
      extraStyles
    } = this.props;
    const { menuOpen, menuOrder, menuPosition } = this.state;
    const { handleMenuPosition, handleMenuToggle, handleSubNavToggle } = this;
    const lastMenuOrderItem = menuOrder.length - 1;

    return (
      <nav className={classNames(styles.menu, extraStyles)}>
        <button
          aria-expanded={menuOpen}
          aria-haspopup="true"
          className={styles.menuButton}
          onClick={handleMenuToggle}
          type="button">
          <span className={classNames(styles.menuButtonText, 'hide-text-relative')}>Open Menu</span>
          <NotificationsCount count={notifications && notifications.count} />
        </button>

        <div className={classNames(styles.menuPopup, menuOpen && styles.isOpen)}>
          <div className={styles.menuPopupHeader}>
            {menuOrder.length > 1 && (
              <button
                className={classNames(styles.menuListItemSubNavBackButton, 'hide-text-relative')}
                onClick={() => handleMenuPosition(true)}
                type="button">
                Back to main menu
              </button>
            )}

            {menuOrder.length === 1 && notifications && (
              <NotificationsLink
                extraStyles={styles.menuPopupHeaderNotificationsLink}
                {...notifications}
              />
            )}

            <h2 className={styles.menuHeading}>{menuOrder[lastMenuOrderItem]}</h2>

            <button
              className={classNames(styles.menuPopupHeaderCloseButton, 'hide-text-relative')}
              onClick={handleMenuToggle}
              type="button">
              Close Menu
            </button>
          </div>

          <div className={styles.menuListWrapper}>
            <ul className={styles.menuList} style={{ transform: `translateX(${menuPosition}rem)` }}>
              {menuItems.map(item => (
                <MenuList
                  handleMenuToggle={handleMenuToggle}
                  handleSubNavToggle={handleSubNavToggle}
                  isNotInView={menuOrder.indexOf(menuOrder[0]) !== lastMenuOrderItem}
                  item={item}
                  key={item.title}
                  menuOrder={menuOrder}
                />
              ))}
            </ul>
          </div>
        </div>

        <button
          aria-hidden="true"
          className={classNames(
            styles.menuOverlay,
            'hide-text-relative',
            menuOpen && styles.isOpen
          )}
          onClick={handleMenuToggle}
          type="button">
          Close Menu
        </button>
      </nav>
    );
  }
}

Menu.propTypes = {
  config: PropTypes.shape({
    menuItems: PropTypes.arrayOf(
      PropTypes.shape({
        icon: adminMenuChildPropTypes.item.icon,
        items: adminMenuChildPropTypes.item.items,
        title: adminMenuChildPropTypes.item.title,

        /** If the `items` prop isn't passed, give it a URL. */
        url: adminMenuChildPropTypes.item.url
      }).isRequired
    ).isRequired,
    notifications: PropTypes.shape({
      count: PropTypes.number,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  }).isRequired,
  /**
    Use this to pass styles from another component stylesheet into the
    component, e.g. `styles.extraComponentStyles`. **Please note:** we cannot
    guarantee the CSS order when compiling the bundles so we need to increase
    the specificity of the extra styles selectors using `:root` e.g.
    `:root .extra-component-styles` in the CSS file.
  */
  extraStyles: PropTypes.string
};

Menu.defaultProps = {
  extraStyles: ''
};
